import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Bell } from '@sorare/core/src/atoms/icons/Bell';
import { BellOff } from '@sorare/core/src/atoms/icons/BellOff';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import useLifecycle, {
  LIFECYCLE,
  Lifecycle,
} from '@sorare/core/src/hooks/useLifecycle';

import { SetReminderDownloadApp } from './SetReminderDownloadApp';
import { SetReminder_footballRivalsGame } from './__generated__/index.graphql';
import { useFootballRivalsRemindGame } from './useFootballRivalsRemindGame';

const ButtonWithLargeTapArea = styled(ButtonBase)`
  position: relative;
  &::after {
    content: '';
    position: absolute;
    inset: calc(-1 * var(--double-unit));
  }
`;
const OffIconButton = styled(ButtonWithLargeTapArea)`
  color: rgba(var(--c-rgb-neutral-1000), 0.5);
`;
const OnIconButton = styled(ButtonWithLargeTapArea)`
  color: var(--c-neutral-1000);
`;

type Props = {
  footballRivalsGame: SetReminder_footballRivalsGame;
};
export const SetReminder = ({ footballRivalsGame }: Props) => {
  const { shouldNotify, id, slug } = footballRivalsGame;
  const [dialogOpen, setDialogOpen] = useState(false);
  const { currentUser } = useCurrentUserContext();
  const { isMobileApp } = useIsMobileApp();
  const { update: updateLifecycle } = useLifecycle();
  const { formatMessage } = useIntl();
  const { mutate } = useFootballRivalsRemindGame();
  const { isAndroidApp, postMessage } = useIsMobileApp();
  const {
    flags: { usePostMessageEnablePushNotifications = false },
  } = useFeatureFlags();

  const doNotShowRivalsDownloadApp = (
    currentUser?.userSettings.lifecycle as Lifecycle
  )?.doNotShowRivalsDownloadApp;

  const turnNotifOn = () => {
    if (isAndroidApp && usePostMessageEnablePushNotifications) {
      postMessage('enablePushNotifications');
    }
    mutate({
      variables: { input: { gameId: id, shouldNotify: true } },
      slug,
    });
    if (!isMobileApp && !doNotShowRivalsDownloadApp) {
      setDialogOpen(true);
    }
  };

  const turnNotifOff = async () => {
    mutate({
      variables: { input: { gameId: id, shouldNotify: false } },
      slug,
    });
  };

  const onCloseDialog = (doNotShowAgain: boolean) => {
    setDialogOpen(false);

    if (doNotShowAgain) {
      updateLifecycle(LIFECYCLE.doNotShowRivalsDownloadApp, true);
    }
  };

  return (
    <>
      <SetReminderDownloadApp open={dialogOpen} onClose={onCloseDialog} />
      {shouldNotify ? (
        <OnIconButton onClick={turnNotifOff}>
          <Bell
            width={16}
            height={16}
            title={formatMessage({
              id: 'SetReminder.turnOffIconTitle',
              defaultMessage: 'Turn notifications off for this match',
            })}
          />
        </OnIconButton>
      ) : (
        <OffIconButton onClick={turnNotifOn}>
          <BellOff
            width={16}
            height={16}
            title={formatMessage({
              id: 'SetReminder.turnOnIconTitle',
              defaultMessage: 'Turn notifications on for this match',
            })}
          />
        </OffIconButton>
      )}
    </>
  );
};

SetReminder.fragments = {
  footballRivalsGame: gql`
    fragment SetReminder_footballRivalsGame on FootballRivalsGame {
      slug
      id
      shouldNotify
    }
  ` as TypedDocumentNode<SetReminder_footballRivalsGame>,
};
